import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { VehicleCard } from "@/components/VehicleCard";
import { FinancingCalculator } from "@/components/FinancingCalculator";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ShieldCheck, Gauge, BadgeDollarSign, Clock, FileCheck, CheckCircle2, Car, Star, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1615884363252-983eed162938?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

const WHY = [
  { icon: ShieldCheck, title: "Secure & Transparent", desc: "Bank-grade security with full transparency on every financing term — no hidden fees." },
  { icon: BadgeDollarSign, title: "Flexible Down Payments", desc: "Start from just 10% down and tailor your plan with options up to 40% or a custom amount." },
  { icon: Clock, title: "Terms up to 48 Months", desc: "Choose the repayment schedule that fits your budget, from 12 to 48 months." },
  { icon: Car, title: "Premium Inventory", desc: "A hand-selected lineup of America's most sought-after vehicles, inspected and ready." },
];

const STEPS = [
  { icon: Car, title: "Choose Your Vehicle", desc: "Browse our premium inventory and find the perfect match." },
  { icon: Gauge, title: "Estimate Payments", desc: "Use our calculator to see your estimated monthly payment instantly." },
  { icon: FileCheck, title: "Apply Online", desc: "Complete a secure multi-step application and upload your documents." },
  { icon: CheckCircle2, title: "Drive Away", desc: "Once approved, track your financing and payments from your dashboard." },
];

const TESTIMONIALS = [
  { name: "Marcus T.", role: "Dallas, TX", text: "The whole process was seamless. I estimated my payment, applied online, and tracked everything from my dashboard. Truly premium." },
  { name: "Elena R.", role: "Miami, FL", text: "Flexible down payment options made all the difference. F&L Cars gave me terms that actually fit my budget." },
  { name: "David K.", role: "Los Angeles, CA", text: "Transparent, fast, and professional. The financing calculator set clear expectations before I ever applied." },
];

const FAQ = [
  { q: "What is the minimum down payment?", a: "The minimum down payment is 10% of the vehicle price. You can choose a higher amount — 15%, 20%, up to 40%, or a custom figure — to lower your monthly payment." },
  { q: "What financing terms are available?", a: "We offer terms of 12, 18, 24, 30, 36, 42, and 48 months. The maximum financing term is 48 months." },
  { q: "Are the calculator results a guaranteed offer?", a: "No. All calculator results are estimates only and do not constitute a financing offer or approval. Final terms are confirmed in a formally approved financing agreement." },
  { q: "How are my documents handled?", a: "Documents such as your driver's license or proof of address are stored in private, encrypted storage and are never publicly accessible." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);

  useEffect(() => {
    api.get("/vehicles?featured=true").then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
    api.get("/vehicles?sort=newest&limit=3").then((r) => setLatest(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative -mt-20 flex min-h-[92vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Premium vehicle" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#EEF3FC] via-[#EEF3FC]/85 to-[#EEF3FC]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#EEF3FC] via-transparent to-[#EEF3FC]/40" />
        </div>
        <div className="container-x relative z-10 pt-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
              <Star className="h-3 w-3" fill="currentColor" /> Premium US Auto Financing
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Premium Vehicles.<br /><span className="text-gradient-blue">Flexible Financing.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-700">
              Discover America's most in-demand vehicles paired with financing built around you. From 10% down and terms up to 48 months — all managed from one elegant dashboard.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild className="btn-gold rounded-full px-8 py-6 text-base">
                <Link to="/inventory" data-testid="hero-browse-btn">Browse Inventory <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-white/20 bg-slate-100 px-8 py-6 text-base text-slate-900 hover:bg-slate-200 hover:text-slate-900">
                <Link to="/financing" data-testid="hero-financing-btn">How Financing Works</Link>
              </Button>
            </div>
            <div className="mt-10 flex gap-8">
              {[["15+", "Premium Models"], ["10%", "Min. Down"], ["48mo", "Max Term"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-2xl font-extrabold text-slate-900">{n}</div>
                  <div className="text-xs uppercase tracking-wider text-slate-600">{l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured */}
      <section className="relative z-10 py-20 lg:py-28">
        <div className="container-x">
          <motion.div {...fade} className="mb-10 flex items-end justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Handpicked</p>
              <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 lg:text-4xl">Featured Vehicles</h2>
            </div>
            <Link to="/inventory" className="hidden items-center gap-1 text-sm text-slate-700 hover:text-gold sm:flex">View all <ArrowRight className="h-4 w-4" /></Link>
          </motion.div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featured.map((v, i) => (
              <motion.div key={v.id} {...fade} transition={{ duration: 0.5, delay: i * 0.05 }}>
                <VehicleCard vehicle={v} index={i} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator + How financing */}
      <section className="relative z-10 border-y border-slate-200 bg-[#E3ECFB] py-20 lg:py-28">
        <div className="container-x grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-start">
          <motion.div {...fade}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Plan with confidence</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 lg:text-4xl">Estimate Your Monthly Payment</h2>
            <p className="mt-4 text-slate-600">Adjust your down payment, term, and APR to see a real-time estimate. Results are illustrative until a financing agreement is formally approved.</p>
            <div className="mt-8 space-y-5">
              {STEPS.map((s, i) => (
                <div key={s.title} className="flex gap-4">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-gold/30 bg-gold/10 text-gold">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{i + 1}. {s.title}</h4>
                    <p className="text-sm text-slate-600">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div {...fade}><FinancingCalculator initialPrice={46990} /></motion.div>
        </div>
      </section>

      {/* Latest inventory */}
      {latest.length > 0 && (
        <section className="relative z-10 py-20">
          <div className="container-x">
            <motion.h2 {...fade} className="mb-10 font-display text-3xl font-bold text-slate-900 lg:text-4xl">Latest Arrivals</motion.h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {latest.map((v, i) => <VehicleCard key={v.id} vehicle={v} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Why choose */}
      <section className="relative z-10 bg-[#E3ECFB] py-20 lg:py-28">
        <div className="container-x">
          <motion.div {...fade} className="mx-auto mb-14 max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">The F&L Difference</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 lg:text-4xl">Why Choose F&amp;L Cars</h2>
          </motion.div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => (
              <motion.div key={w.title} {...fade} transition={{ duration: 0.5, delay: i * 0.05 }} className="card-surface p-7 hover-lift">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl gold-gradient text-[#EEF3FC]"><w.icon className="h-6 w-6" /></div>
                <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 py-20 lg:py-28">
        <div className="container-x">
          <motion.h2 {...fade} className="mb-12 text-center font-display text-3xl font-bold text-slate-900 lg:text-4xl">What Our Customers Say</motion.h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={t.name} {...fade} transition={{ duration: 0.5, delay: i * 0.05 }} className="card-surface p-7">
                <div className="flex gap-1 text-gold">{[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4" fill="currentColor" />)}</div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">"{t.text}"</p>
                <div className="mt-5 border-t border-slate-200 pt-4">
                  <p className="font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 bg-[#E3ECFB] py-20 lg:py-28">
        <div className="container-x max-w-3xl">
          <motion.h2 {...fade} className="mb-10 text-center font-display text-3xl font-bold text-slate-900 lg:text-4xl">Frequently Asked Questions</motion.h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="card-surface border-slate-200 px-5" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-slate-900 hover:text-gold hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 overflow-hidden py-24">
        <div className="container-x">
          <div className="glass relative overflow-hidden rounded-3xl p-10 text-center lg:p-16">
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-gold/10 blur-3xl" />
            <h2 className="font-display text-3xl font-extrabold text-slate-900 lg:text-5xl">Ready to Drive Your Dream?</h2>
            <p className="mx-auto mt-4 max-w-xl text-slate-700">Explore our premium inventory and start a secure financing application in minutes.</p>
            <Button asChild className="btn-gold mt-8 rounded-full px-10 py-6 text-base">
              <Link to="/inventory" data-testid="cta-browse-btn">Explore Inventory <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
